// Discover.jsx
import { useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import api from "../api";

import ActivityCard from "../components/ActivityCard";
import ActivityDetailModal from "../components/ActivityDetailModal";

function normalizeToArray(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.activities)) return raw.activities;
    return [];
}

function normalizeDisplaySection(activity) {
    return String(activity?.display_section || "activities").trim().toLowerCase();
}

function isHiddenActivity(activity) {
    return normalizeDisplaySection(activity) === "hidden";
}

function isPassPerkActivity(activity) {
    const section = normalizeDisplaySection(activity);
    return section === "pass_perks" || activity?.is_pass_perk === true;
}

function getDisplayOrder(activity) {
    const raw = activity?.display_order;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : 999;
}

function sortBySupabaseDisplayOrder(a, b) {
    const orderDiff = getDisplayOrder(a) - getDisplayOrder(b);
    if (orderDiff !== 0) return orderDiff;

    return String(a?.name || "").localeCompare(String(b?.name || ""));
}

export default function Discover() {
    const isNative = Capacitor.isNativePlatform();

    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedActivity, setSelectedActivity] = useState(null);

    useEffect(() => {
        let alive = true;

        async function loadActivities() {
            try {
                const res = await api.get("/activities");
                const list = normalizeToArray(res?.data);

                if (!alive) return;
                setActivities(list.filter((activity) => !isHiddenActivity(activity)));
            } catch (err) {
                console.error("Discover: failed to load /activities", err);

                if (!alive) return;
                setActivities([]);
            } finally {
                if (!alive) return;
                setLoading(false);
            }
        }

        loadActivities();

        return () => {
            alive = false;
        };
    }, []);

    const passPerks = useMemo(() => {
        const list = Array.isArray(activities) ? activities : [];
        return list
            .filter((activity) => isPassPerkActivity(activity))
            .sort(sortBySupabaseDisplayOrder);
    }, [activities]);

    const normalActivities = useMemo(() => {
        const list = Array.isArray(activities) ? activities : [];
        return list
            .filter((activity) => !isPassPerkActivity(activity))
            .sort(sortBySupabaseDisplayOrder);
    }, [activities]);

    return (
        <div className={`discover-page ${isNative ? "discover-page--app" : "discover-page--web"}`}>
            {!isNative ? (
                <header className="discover-web-hero discover-web-hero--fred">
                    <div className="discover-web-hero__copy">
                        <div className="discover-kicker">Explore the Coast</div>

                        <h1>Discover Coastal Adventures</h1>

                        <p>
                            Browse Beach Bum Pass activities, local favorites, and partner perks
                            across Fort Walton Beach, Okaloosa Island, and the Emerald Coast.
                        </p>
                    </div>

                    <div className="discover-web-hero__mascot" aria-hidden="true">
                        <div className="discover-mascot-card">
                            <div className="discover-mascot-card__sun" />

                            <img
                                className="discover-mascot-card__fred"
                                src="/assets/Graphics/Fred_Showing_Pass.png"
                                alt=""
                                onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                }}
                            />

                            <div className="discover-mascot-card__pass">
                                <span>Beach Bum Pass</span>
                                <strong>Scan & Go</strong>
                                <small>Show your pass. Start the fun.</small>
                            </div>
                        </div>
                    </div>
                </header>
            ) : null}

            {isNative ? (
                <header className="app-page-hero app-page-hero--explore discover-header discover-header--app">
                    <div className="app-page-kicker discover-kicker"><span className="discover-kicker-mark" aria-hidden="true" />Explore</div>
                    <h1>Coastal Adventures</h1>
                    <p>Open an experience, check the details, and head out when you’re ready.</p>
                </header>
            ) : null}

            {loading ? (
                <section className="discover-shell discover-loading-shell">
                    <p className="loading-text">Loading experiences...</p>
                </section>
            ) : (
                <>
                    <section className="discover-shell discover-results-shell">
                        <div className="discover-section-head discover-section-head--prominent">
                            <div>
                                <h2>Activities</h2>
                            </div>

                            
                        </div>

                        {normalActivities.length === 0 ? (
                            <div className="discover-empty-state">
                                <strong>No activities found yet.</strong>
                                <p>Activities will appear here once they are added.</p>
                            </div>
                        ) : (
                            <div className="activity-grid discover-activity-grid">
                                {normalActivities.map((activity) => (
                                    <ActivityCard
                                        key={activity.id}
                                        activity={activity}
                                        onView={setSelectedActivity}
                                    />
                                ))}
                            </div>
                        )}
                    </section>

                    {passPerks.length > 0 ? (
                        <section className="discover-shell discover-perks-section">
                            <div className="discover-section-head discover-section-head--prominent discover-perks-head">
                                <div>
                                    <h2>Pass Perks</h2>
                                </div>

                               
                            </div>

                            <div className="activity-grid discover-activity-grid activity-grid--perks">
                                {passPerks.map((perk) => (
                                    <ActivityCard
                                        key={perk.id}
                                        activity={perk}
                                        onView={setSelectedActivity}
                                    />
                                ))}
                            </div>
                        </section>
                    ) : null}
                </>
            )}

            <ActivityDetailModal
                activity={selectedActivity}
                onClose={() => setSelectedActivity(null)}
            />
        </div>
    );
}